import { Card } from 'antd';
import React from 'react';
import { connect } from 'react-redux';
import {
	ArrowRightOutlined,
	EditOutlined,
	PlusCircleOutlined,
	RightCircleOutlined,
} from '@ant-design/icons';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { UserName } from '../name_component/user/UserNameComponent';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { assetAudits } from '../../thunks/assets_thunks';
import { Link } from 'react-router-dom';
import DateTimeHover from '../date_time_component/DateTime';
import { withUserType } from '../withUserType';
import OWPaginatedTimeline from '../common/OWPaginatedTimeline';
import AuditChangeComponent from '../common/audit/AuditChangeComponent';
import { getAuditDiffForJsonValue } from '../common/audit/auditDiffUtils';

const fieldMap = {
	name: 'Name',
	isActive: 'Is Active',
	description: 'Description',
	manufacturer: 'Manufacturer',
	modelNumber: 'Model Number',
	serialNumber: 'Serial Number',
	assetNumber: 'Asset Number',
	locationId: 'Location',
	assetTypeId: 'Asset Type',
	assetModelId: 'Asset Model',
	installDate: 'Install Date',
	isLeased: 'Is Leased',
	purchaseDate: 'Purchase Date',
	lifeExpectancyInMonths: 'Life Expectancy In Months',
	originalPrice: 'Original Price',
	capitalisedCost: 'Capitalised Cost',
	leaseStartDate: 'Lease Start Date',
	leaseEndDate: 'Lease End Date',
	warrantyPeriod: 'Warranty Period',
	NTEAmount: 'NTE Amount',
	provider: 'Provider',
	assetTypeDetails: 'Asset Type Details',
	images: 'Images',
	assetUUID: 'Asset UUID',
	buyerFacilityId: 'Buyer Facility',
	warrantyId: 'Warranty',
	id: 'Id',
	downtimeStatus: 'Downtime Status',
};

const ASSET_PROPS_TO_HIDE = ['buyerSpecificAssetId'];

function AssetAudit({ match, fetchAssetAudits, userType, asset }) {
	const assetId = match.params.id;

	const getDescription = (item) => {
		const renderDefaultChip = (field) => (
			<span className="inline-block rounded-md bg-violet-100 p-1 text-sm text-gray-500">
				{fieldMap[field.fieldName] || field.fieldName} : {field.oldValue || `' '`}{' '}
				<ArrowRightOutlined /> {field.newValue}
			</span>
		);

		const renderFieldChange = (field) => {
			switch (field.fieldName) {
				case 'assetTypeDetails':
					return getAuditDiffForJsonValue(field).map((changeProp, idx) => (
						<AuditChangeComponent key={`assetTypeDetails-change-${idx}`} {...changeProp} />
					));
				case 'originalPrice':
				case 'capitalisedCost':
					return (
						<AccessPermissionChecker name={PERMISSION_NAMES.SHOW_ASSET_PRICE}>
							{renderDefaultChip(field)}
						</AccessPermissionChecker>
					);
				default:
					return renderDefaultChip(field);
			}
		};

		const getFieldsChange = (item) => {
			return (
				<div className="mt-3 flex flex-wrap gap-2">
					{item.fieldValueChanges &&
						item.fieldValueChanges
							.filter((field) => !ASSET_PROPS_TO_HIDE.includes(field.fieldName))
							.map(renderFieldChange)}
					{item && item.toAssetType ? (
						<span className="inline-block rounded-md bg-violet-100 p-1 text-sm text-gray-500">
							Asset Type : {nullSafeGetOrElse('fromAssetType.name', item, `' '`)}{' '}
							<ArrowRightOutlined /> {nullSafeGetOrElse('toAssetType.name', item, `' '`)}
						</span>
					) : null}
					{item && item.toAssetModel ? (
						<span className="inline-block rounded-md bg-violet-100 p-1 text-sm text-gray-500">
							Asset Model : {nullSafeGetOrElse('fromAssetModel.modelName', item, `' '`)}{' '}
							<ArrowRightOutlined /> {nullSafeGetOrElse('toAssetModel.modelName', item, `' '`)}
						</span>
					) : null}
					{item && item.toArea ? (
						<span className="inline-block rounded-md bg-violet-100 p-1 text-sm text-gray-500">
							Area : {nullSafeGetOrElse('fromArea.name', item, `' '`)} <ArrowRightOutlined />{' '}
							{nullSafeGetOrElse('toArea.name', item, `' '`)}
						</span>
					) : null}
				</div>
			);
		};

		const getTransferDateIfAvailable = () => {
			return item.transferDate ? (
				<span className="ml-2 inline-block">
					<DateTimeHover
						timestamp={nullSafeGet('transferDate', item)}
						timeZone={nullSafeGet('location.timeZoneId', asset)}
					/>
				</span>
			) : null;
		};

		switch (item.auditType) {
			case 'asset_create':
				return 'Asset created';
			case 'manual_edit':
				return (
					<div>
						Edited by <UserName contact={item.createdByContact} mode={'inline'} placement={'top'} />
						{getFieldsChange(item)}
					</div>
				);
			case 'location_transfer':
				return (
					<div>
						Location transferred from{' '}
						<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
							{nullSafeGetOrElse('fromLocation.name', item, '')}
						</span>{' '}
						to{' '}
						<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
							{nullSafeGetOrElse('toLocation.name', item, '')}
						</span>{' '}
						by <UserName contact={item.createdByContact} mode={'inline'} placement={'top'} />
						{getTransferDateIfAvailable()}
						{getFieldsChange(item)}
					</div>
				);
			case 'location_to_stock_location_transfer':
				return (
					<div>
						Transferred from{' '}
						<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
							{nullSafeGetOrElse('fromLocation.name', item, '')}
						</span>{' '}
						to stock location{' '}
						<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
							{nullSafeGetOrElse('transferredToStockLocation.name', item, '')}
						</span>{' '}
						by <UserName contact={item.createdByContact} mode={'inline'} placement={'top'} />
						{getTransferDateIfAvailable()}
						{getFieldsChange(item)}
					</div>
				);
			case 'stock_location_transfer':
				return (
					<div>
						Stock location transferred from{' '}
						<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
							{nullSafeGetOrElse('transferredFromStockLocation.name', item, '')}
						</span>{' '}
						to{' '}
						<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
							{nullSafeGetOrElse('transferredToStockLocation.name', item, '')}
						</span>{' '}
						by <UserName contact={item.createdByContact} mode={'inline'} placement={'top'} />
						{getTransferDateIfAvailable()}
						{getFieldsChange(item)}
					</div>
				);
			case 'stock_location_to_location_transfer':
				return (
					<div>
						Transferred from stock location{' '}
						<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
							{nullSafeGetOrElse('transferredFromStockLocation.name', item, '')}
						</span>{' '}
						to location{' '}
						<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
							{nullSafeGetOrElse('toLocation.name', item, '')}
						</span>{' '}
						by <UserName contact={item.createdByContact} mode={'inline'} placement={'top'} />
						{getTransferDateIfAvailable()}
						{getFieldsChange(item)}
					</div>
				);
			case 'burden_rate_change':
				return (
					<div>
						Re-calculated capitalised cost due to burden rate change by{' '}
						<UserName contact={item.createdByContact} mode={'inline'} placement={'top'} />
						{getFieldsChange(item)}
					</div>
				);
			case 'work_order_create':
				return (
					<div>
						Work Order created{' '}
						<Link
							to={`/${userType}/workOrders/detail/${nullSafeGetOrElse(
								'workOrder.id',
								item,
								''
							)}/details`}
						>
							<span className="inline-block rounded-md bg-violet-200 p-1 text-sm text-gray-600">
								{nullSafeGetOrElse('workOrder.title', item, '')}
							</span>{' '}
						</Link>
						by <UserName contact={item.createdByContact} mode={'inline'} placement={'top'} />
						{getFieldsChange(item)}
					</div>
				);
		}
	};
	const getIcon = (item) => {
		switch (item.auditType) {
			case 'asset_create':
				return <PlusCircleOutlined style={{ color: '#000000' }} />;
			case 'manual_edit':
				return <EditOutlined style={{ color: '#000000' }} />;
			default:
				return <RightCircleOutlined style={{ color: '#000000' }} />;
		}
	};
	return (
		<div className="flex flex-col p-4">
			<Card>
				<div className="flex">
					<OWPaginatedTimeline
						fetchData={fetchAssetAudits}
						renderRecord={(item) => (
							<div className="mb-5 pt-1">
								<div className=" text-base ">{getDescription(item)}</div>
								<span className="mt-3 inline-block text-sm text-gray-500">
									<DateTimeHover
										timestamp={nullSafeGet('createdAt', item)}
										timeZone={nullSafeGet('location.timeZoneId', asset)}
									/>
								</span>
							</div>
						)}
						getDot={getIcon}
						additionalParams={{ assetId }}
					/>
				</div>
			</Card>
		</div>
	);
}

const mapStateToProps = (state, ownProps) => ({
	asset: getEntityById(ownProps.match.params.id, state.assets),
	userType: state.session.userType,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchAssetAudits: (params) => dispatch(assetAudits(ownProps.userType)(params)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(AssetAudit));
